import { ReactElement, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTimeout } from "../../hooks/index";
import { ToastContext } from "./toast-context";
import { AlertVariants } from "../../sharedTypes/components";
import "./toasts.scss";

type ToastProperties = {
  message: string;
  variant: AlertVariants;
  close: () => void;
  title?: string;
};

export function Toast({ title, message, variant, close }: Readonly<ToastProperties>) {
  useTimeout(() => {
    close();
  }, 5000);

  return (
    <div className={`toast ${variant} outlined`}>
      <div>
        {title && <p className="title">{title}</p>}
        <span className="subtitle">{message}</span>
      </div>
    </div>
  );
}

export type ToastType = {
  id: number;
  message: string;
  variant: AlertVariants;
  title?: string;
};

export function ToastProvider({ children }: Readonly<{ children: ReactElement }>) {
  const { pathname } = useLocation();
  const centerToast = pathname.split("/").includes("auth");
  const [toasts, setToasts] = useState<ToastType[]>([]);

  function openToast({ message, variant, title }: Omit<ToastType, "id">) {
    const newToast = {
      title,
      message,
      variant,
      id: Date.now(),
    };
    setToasts((prevToasts) => [newToast, ...prevToasts]);
  }

  function closeToast(id: number) {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }

  const contextValue = useMemo(
    () => ({
      open: openToast,
      close: closeToast,
    }),
    []
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div className={`toasts ${centerToast ? "" : "centered"}`}>
        {toasts &&
          toasts.map(({ id, title, message, variant }) => {
            return (
              <Toast
                key={id}
                title={title}
                message={message}
                variant={variant}
                close={() => closeToast(id)}
              />
            );
          })}
      </div>
    </ToastContext.Provider>
  );
}
